import { CSSProperties, ReactNode } from 'react';
import type { User } from '@supabase/supabase-js';
import { Capacitor } from '@capacitor/core';
import { useSession, buildSignInUrl } from '../hooks/useSession';
import { BrandGlyph, MODULE_COLOR } from '../lib/branding';
import { NativeSignIn } from './NativeSignIn';

/* Any native Capacitor shell (iOS or Android) shows the in-app
 * email/password form instead of bouncing to plan.cadtomie.com.
 *
 * Why not just protocol sniffing? iOS Capacitor serves the bundle
 * from `capacitor://localhost`, but ANDROID Capacitor serves it
 * from plain `http://localhost`. The earlier `protocol ===
 * 'capacitor:'` check missed Android entirely — Android users
 * fell through to the web SignInGate which redirected to
 * `http://localhost:5173` (the dev planner) and threw a network
 * error.  `Capacitor.isNativePlatform()` is the official API
 * and covers both platforms in one call. */
const isNativeShell =
  typeof window !== 'undefined' && Capacitor.isNativePlatform();

/* ============================================================
   RequireSession
   ------------------------------------------------------------
   Wraps the Intraop measurement routes (`/app/*`) and only
   renders them when the user is signed in AND their email is
   verified. Marketing routes (`/`, `/privacy`, `/terms`,
   `/impressum`) stay public — anyone can read what the product
   does without an account.

   Sign-in lives on plan.cadtomie.com so there is one login
   form to maintain. After a successful sign-in the planner
   bounces the user back here automatically via `?return=`.
   ============================================================ */

interface Props {
  children: ReactNode;
}

export function RequireSession({ children }: Props) {
  const { session, loading } = useSession();

  if (loading) {
    // The cookie read is essentially synchronous; render nothing for
    // the one-tick window to avoid a flash of the sign-in gate.
    return null;
  }

  if (!session) {
    return isNativeShell ? <NativeSignIn /> : <SignInGate />;
  }

  // Supabase v2 exposes the verification timestamp under
  // `email_confirmed_at`; older sessions sometimes only have
  // `confirmed_at`. We accept either.
  const u = session.user as User & {
    email_confirmed_at?: string | null;
    confirmed_at?: string | null;
  };
  const verified = Boolean(u.email_confirmed_at || u.confirmed_at);
  if (!verified) {
    return <UnverifiedGate email={session.user.email ?? ''} />;
  }

  return <>{children}</>;
}

/* ─────────────────── Sign-in gate ─────────────────── */

function SignInGate() {
  const returnTo =
    typeof window !== 'undefined' ? window.location.href : '';
  const signInUrl = buildSignInUrl(returnTo);

  return (
    <main className="safe-top safe-bottom" style={pageStyle}>
      <Atmosphere />
      <div style={cardStyle}>
        <BrandGlyph size={48} color={MODULE_COLOR.angle} />
        <h1 style={titleStyle}>Sign in to use Intraop</h1>
        <p style={subtitleStyle}>
          One CADtomie account works for both the planner and the
          intraoperative tools. Once you're in, both modules unlock — no
          second login needed.
        </p>
        <a href={signInUrl} style={primaryBtnStyle}>
          Continue to sign in →
        </a>
        <p style={hintStyle}>
          New here? You can create a free account on the same screen.
        </p>
        <p style={smallStyle}>
          You'll be sent to plan.cadtomie.com to sign in, then bounced
          back here automatically.
        </p>
      </div>
    </main>
  );
}

/* ─────────────────── Unverified gate ─────────────────── */

function UnverifiedGate({ email }: { email: string }) {
  const { signOut } = useSession();
  return (
    <main className="safe-top safe-bottom" style={pageStyle}>
      <Atmosphere />
      <div style={cardStyle}>
        <BrandGlyph size={48} color={MODULE_COLOR.height} />
        <h1 style={titleStyle}>Check your email</h1>
        <p style={subtitleStyle}>
          We sent a verification link to{' '}
          <strong style={{ color: 'var(--text)' }}>{email}</strong>. Click
          it, then come back here.
        </p>
        <button
          type="button"
          style={primaryBtnStyle}
          onClick={() => window.location.reload()}
        >
          I've verified — refresh
        </button>
        <button
          type="button"
          style={ghostBtnStyle}
          onClick={async () => {
            await signOut();
            window.location.reload();
          }}
        >
          Use a different account
        </button>
      </div>
    </main>
  );
}

/* ─────────────────── Background atmosphere ─────────────────── */

function Atmosphere() {
  return (
    <div
      aria-hidden
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
        background:
          'radial-gradient(60% 40% at 50% 0%, rgba(124,196,255,0.08), transparent 60%), ' +
          'radial-gradient(70% 50% at 50% 100%, rgba(124,196,255,0.04), transparent 60%)',
      }}
    />
  );
}

/* ─────────────────── Styles ─────────────────── */

const pageStyle: CSSProperties = {
  position: 'relative',
  minHeight: '100dvh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  background: 'var(--bg)',
};

const cardStyle: CSSProperties = {
  position: 'relative',
  zIndex: 1,
  maxWidth: 420,
  width: '100%',
  background: 'var(--surface-strong)',
  border: '1px solid var(--hairline)',
  borderRadius: 'var(--radius-lg)',
  padding: '36px 28px 28px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 14,
  textAlign: 'center',
  boxShadow: 'var(--shadow)',
};

const titleStyle: CSSProperties = {
  margin: '8px 0 0 0',
  color: 'var(--text)',
  fontSize: 22,
  fontWeight: 700,
  letterSpacing: '-0.02em',
};

const subtitleStyle: CSSProperties = {
  margin: 0,
  color: 'var(--text-dim)',
  fontSize: 15,
  lineHeight: 1.5,
};

const primaryBtnStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  marginTop: 8,
  background: 'var(--accent)',
  color: '#0a0a0a',
  border: 'none',
  borderRadius: 999,
  padding: '14px 24px',
  fontSize: 16,
  fontWeight: 600,
  textDecoration: 'none',
  width: '100%',
  maxWidth: 320,
  cursor: 'pointer',
  font: 'inherit',
};

const ghostBtnStyle: CSSProperties = {
  background: 'transparent',
  color: 'var(--text-dim)',
  border: 'none',
  padding: '8px 12px',
  fontSize: 14,
  cursor: 'pointer',
  font: 'inherit',
};

const hintStyle: CSSProperties = {
  margin: 0,
  color: 'var(--text-dim)',
  fontSize: 13,
};

const smallStyle: CSSProperties = {
  margin: 0,
  color: 'var(--text-muted)',
  fontSize: 12,
  marginTop: 4,
};
